import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function HireAFoodTruck() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Hire a Food ${vehicle_type} for Your Event`}
        type='website'
        canonical='/hire-a-food-truck'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Hire a food ${vehicleType} for your next event in ${city_name}. From corporate lunches to private parties, find and book the best food ${vehicleType}s in ${city_name}.`}
        structuredData={{
          cateringService: {
            name: `Hire a Food ${vehicle_type} for Your Event in ${city_name}`,
            description: `Hire a food ${vehicleType} for your next event in ${city_name}. From corporate lunches to private parties.`,
            url: '/hire-a-food-truck',
            serviceType: 'Food Truck Hire',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Hire a Food Truck', item: '/hire-a-food-truck' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Hire a Food {vehicle_type} for Your Next Event</h1>
            <p>Want to hire a food {vehicleType} for your upcoming event? {site_title} makes it simple to find, compare, and book the best food {vehicleType}s in {city_name}. Whether you&#39;re planning a corporate lunch, a private party, or a community gathering, hiring a food {vehicleType} is the easiest way to serve fresh, delicious food to your guests. Tell us about your event and get free quotes from top local {vehicleType}s.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Get a Free Quote</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Top Food ${vehicle_type}s Available for Hire`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Hire a Food ${vehicle_type}?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Hire a Food {vehicle_type} for Any Occasion</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Corporate Events & Office Lunches</h3>
            <p>Hiring a food {vehicleType} is one of the most popular ways to <Link to='/catering'>cater a corporate event</Link> in {city_name}. From team lunches and employee appreciation days to product launches and company milestones, a hired food {vehicleType} delivers a unique, hassle-free dining experience that your team will love.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Private Parties & Celebrations</h3>
            <p>From birthday parties to graduation celebrations, hiring a food {vehicleType} adds a memorable touch to any private event. Guests love the interactive experience and variety of fresh, made-to-order food. You can also <Link to='/food-truck-rental'>rent a food {vehicleType}</Link> for weddings, anniversaries, and other special occasions throughout {city_name}.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Community Events & Fundraisers</h3>
            <p>Hiring a food {vehicleType} is a great option for block parties, festivals, fundraisers, and school events. Food {vehicleType}s bring energy and variety to community gatherings, and they handle all the cooking and serving so organizers can focus on the event itself. {site_title} helps you find the right {vehicleType} for your budget and crowd size.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Hire a Food ${vehicle_type} for Your Next Event!`} />
      </section>
    </div>
  );
}

export default HireAFoodTruck;
